import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./database.types";

export class SupabaseNotConfiguredError extends Error {
  constructor(
    message = "Supabase is not configured (missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)",
  ) {
    super(message);
    this.name = "SupabaseNotConfiguredError";
  }
}

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Native Supabase Auth: supabase-js manages the session (persisted in
// localStorage) and attaches it to every request automatically, so RLS can
// authenticate via `auth.uid()`. Signed-out requests fall back to the anon
// key, so user-scoped rows stay denied.
export const supabase: SupabaseClient<Database> | null =
  url && anonKey
    ? createClient<Database>(url, anonKey, {
        auth: { persistSession: true, detectSessionInUrl: true },
        global: { headers: { "x-client-info": "lexibit-web@0.1.0" } },
      })
    : null;

export const isSupabaseConfigured = supabase !== null;

/** Returns the current Supabase access token, or null if not signed in. */
export async function getSupabaseAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
